import React from "react";
import { Link } from "react-router-dom";

const plans = [
  {
    name: "Essential",
    duration: "2 Hours",
    tagline: "Perfect for smaller gatherings & private parties.",
    highlights: ["Open Air Booth", "Instant Sharing", "Attendant Included"],
    includes: [
      "Unlimited sessions during rental time",
      "Standard backdrop & premium props",
      "Event-branded photo overlay",
      "Digital delivery via QR / SMS / Email",
      "Priority setup & breakdown"
    ],
    highlight: false,
  },
  {
    name: "Premium",
    duration: "3–4 Hours",
    tagline: "Most popular for weddings & corporate events.",
    highlights: ["Open Air or Glam", "GIFs + Video Snippets", "Online Gallery"],
    includes: [
      "Premium backdrops + curated prop set",
      "Custom overlay with your logo / theme",
      "Unlimited sessions & instant digitals",
      "Post-event online gallery",
      "Priority setup & breakdown"
    ],
    highlight: true,
  },
  {
    name: "Deluxe",
    duration: "Up to 5 Hours",
    tagline: "Best for large events & brand activations.",
    highlights: ["360° / Glam Options*", "Full Branding", "Data Capture (Brands)"],
    includes: [
      "Fully custom-branded overlays & screens",
      "Sharing station + priority setup",
      "Branded microsite / gallery",
      "Basic analytics (for brands)",
      "Priority setup & breakdown"
    ],
    highlight: false,
    note: "*360° availability depends on venue space & access.",
  },
];

export default function PricingSection() {
  return (
    <section
      id="pricing"
      className="py-20 bg-gradient-to-b from-[#060714] via-[#050815] to-[#05030D] text-white"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12 animate-fade-up">
          <p className="text-xs uppercase tracking-[0.3em] text-[#E34364] mb-2">
            Packages
          </p>
          <h2 className="text-3xl md:text-4xl font-bold mb-3">
            Choose Your Experience
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto text-sm md:text-base">
            Pricing varies by venue, customization, and booth type. Choose a package and
            we’ll send you a tailored quote.
          </p>
        </div>

        {/* Cards */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {plans.map((plan, idx) => (
            <article
              key={plan.name}
              className={`relative rounded-2xl overflow-hidden border transition-all duration-300 animate-fade-up
                ${
                  plan.highlight
                    ? "border-[#E34364]/40 bg-white/[0.06] shadow-[0_18px_60px_rgba(227,67,100,0.18)]"
                    : "border-white/10 bg-white/[0.04] shadow-[0_16px_45px_rgba(0,0,0,0.55)]"
                }
                hover:border-white/20 hover:-translate-y-1`}
              style={{ animationDelay: `${idx * 0.08}s` }}
            >
              {/* subtle glow corner */}
              <div
                className={`absolute -top-20 -right-20 w-56 h-56 rounded-full blur-3xl opacity-40
                  ${plan.highlight ? "bg-[#E34364]" : "bg-purple-500"}`}
              />

              <div className="relative p-6">
                {/* top row */}
                <div className="flex items-center justify-between mb-4">
                  {plan.highlight ? (
                    <span className="text-[10px] uppercase tracking-[0.25em] text-pink-100 bg-[#E34364]/20 border border-[#E34364]/30 px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-[0.25em] text-gray-200 bg-white/5 border border-white/10 px-3 py-1 rounded-full">
                      Package
                    </span>
                  )}

                  <span className="text-[11px] text-gray-200 bg-white/5 border border-white/10 px-3 py-1 rounded-full">
                    {plan.duration}
                  </span>
                </div>

                {/* title */}
                <h3
                  className={`text-2xl font-semibold ${
                    plan.highlight ? "text-[#E34364]" : "text-white"
                  }`}
                >
                  {plan.name}
                </h3>
                <p className="text-sm text-gray-300 mt-1 leading-relaxed">
                  {plan.tagline}
                </p>

                {/* highlight bullets (3) */}
                <div className="mt-5 grid gap-2">
                  {plan.highlights.map((h) => (
                    <div
                      key={h}
                      className="flex items-center gap-2 rounded-xl bg-white/5 border border-white/10 px-3 py-2"
                    >
                      <span className="w-2 h-2 rounded-full bg-[#E34364]" />
                      <span className="text-sm text-gray-100">{h}</span>
                    </div>
                  ))}
                </div>

                {/* includes */}
                <div className="mt-5">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-gray-400 mb-2">
                    What’s Included
                  </p>
                  <ul className="space-y-2 text-sm text-gray-200">
                    {plan.includes.map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <span className="mt-[6px] w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {plan.note && (
                    <p className="mt-3 text-[11px] text-gray-400">{plan.note}</p>
                  )}
                </div>

                {/* CTA */}
                <div className="mt-6 flex flex-col gap-2">
                  <Link to="/book"
                  
                    className={`w-full inline-flex justify-center items-center rounded-full text-sm font-semibold py-2.5 transition
                      ${
                        plan.highlight
                          ? "bg-[#E34364] text-white shadow-[0_16px_40px_rgba(227,67,100,0.55)] hover:bg-[#f15477]"
                          : "bg-white/5 text-gray-100 hover:bg-white/10"
                      }`}
                  >
                    Request a Quote
                  </Link>

                  <button
                    onClick={() =>
                      document.getElementById("contact")?.scrollIntoView({
                        behavior: "smooth",
                      })
                    }
                    className="text-xs text-gray-400 hover:text-gray-200 transition"
                  >
                    Want something custom? Tell us your event details →
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>

        <p className="text-center text-xs text-gray-400 mt-10 animate-fade-up">
          Final quotes depend on booth type, duration, venue access, and custom branding.
        </p>
      </div>
    </section>
  );
}
